/**
 * The render layers and the scene tree they form.
 *
 * Every layer is created here at module load, so each module can reach its
 * layer from the start, and SCENE parents them at load too, so the draw order
 * lives here as well. Graphics.ts fills them: the terrain tiles under sea and
 * map, the shade under shadows, the HUD sprites after the ui layers, and
 * doodads into map once the terrain tiles exist, as the mountains draw above
 * them. The names are the TextureLayer union, which TextureConfig uses to
 * place sprites.
 *
 * The camera moves objects, groundobjects, doodads and shadows every frame.
 * The first three are Pixi render groups: the GPU applies the layer's own
 * transform, so a camera move no longer recomputes the transform and vertices
 * of every child, which measured at a sixth to a quarter of a frame's CPU in
 * play. Shadows stays a plain container: Pixi resolves a render group's own
 * children against a blank parent, so the multiply blend the shadow sprites
 * inherit would stop at the group's boundary.
 */
import { Container } from 'pixi.js';
import type { GraphicsLayers } from './types/Graphics';
import type { TextureLayer } from './types/Textures';

/** Every layer by name. */
export const layers: GraphicsLayers = {
  game: new Container(),
  ui0: new Container(),
  ui1: new Container(),
  ui2: new Container(),
  ui3: new Container(),
  ui4: new Container(),
  hudHealth: new Container(),
  hudEnergy: new Container(),
  map: new Container(),
  sea: new Container(),
  objects: new Container({ isRenderGroup: true }),
  groundobjects: new Container({ isRenderGroup: true }),
  shadows: new Container(),
  projectiles: new Container(),
  thrusters: new Container(),
  glows: new Container(),
  crates: new Container(),
  powerups: new Container(),
  flags: new Container(),
  aircraft: new Container(),
  aircraftme: new Container(),
  doodads: new Container({ isRenderGroup: true }),
  fields: new Container(),
  explosions: new Container(),
  playernames: new Container(),
  bubbles: new Container(),
  smoke: new Container(),
};

/** Each parent's layer children in draw order, back to front. */
const SCENE: ReadonlyArray<readonly [parent: TextureLayer, children: readonly TextureLayer[]]> = [
  ['game', ['sea', 'map', 'groundobjects', 'shadows', 'objects', 'ui0', 'ui1', 'ui2', 'ui3', 'ui4']],
  ['groundobjects', ['fields']],
  [
    'objects',
    [
      'smoke',
      'crates',
      'thrusters',
      'projectiles',
      'aircraft',
      'aircraftme',
      'glows',
      'explosions',
      'powerups',
      'playernames',
      'flags',
      'bubbles',
    ],
  ],
];

// The labels name the layers in Pixi's tree tools
for (const [name, container] of Object.entries(layers)) {
  container.label = name;
}
for (const [parent, children] of SCENE) {
  layers[parent].addChild(...children.map((name) => layers[name]));
}
